import {
  SelectOptionType,
  TextInput,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { Control, useFormContext } from 'react-hook-form';

import { unwrapOptionValue } from '../../../utils/selectFormat';
import { EXTRA_FORMATTERS } from '../utils';
import {
  FieldExtraFormatter,
  FormField,
  validationSchema,
} from '../validations/validations';

interface AutofillNationalityFieldProps {
  field: FormField;
  control: Control<any>;
  options?: Record<string, SelectOptionType[]>;
  readOnly?: boolean;
}

/**
 * Wrapper de campo de texto de solo lectura que se autollena a partir de otro
 * campo del formulario (`field.autofillFrom`).
 *
 * Config-driven: este componente solo se usa cuando el JSON del campo declara
 * `autofillFrom`. Los campos que no la declaran siguen el render normal, por lo
 * que los forms de otros tenants no cambian en absoluto (aislamiento por tenant).
 *
 * Caso de uso (HU 16033, Universidad de la Reformada): el campo "Nacionalidad"
 * (`personalInformation_nationality`) es de solo lectura y espeja el "País de
 * Nacimiento" (`personalInformation_countryOfBirth`). Como es `required` y
 * `readonly`, el autofill es obligatorio para poder enviar el formulario.
 */
function AutofillNationalityField({
  field,
  control,
  options,
  readOnly,
}: AutofillNationalityFieldProps) {
  const { watch, setValue } = useFormContext();

  // Valor del campo padre (p.ej. País de Nacimiento). Puede venir como opción
  // completa `{ value, label }` (guardado por el formulario) o como escalar
  // `'CO'` (guardado por los portales de matrícula).
  const parent = watch(field.autofillFrom as string);

  const autofilledLabel = useMemo(() => {
    if (parent === null || parent === undefined || parent === '') {
      return '';
    }

    // Si el padre ya trae la opción completa, su label es la fuente directa.
    if (typeof parent === 'object' && 'label' in parent && parent.label) {
      return String(parent.label);
    }

    // Si el padre es escalar (o no trae label), resolvemos contra el catálogo
    // de países. El label del país es lo que se muestra como nacionalidad.
    const parentValue = unwrapOptionValue(parent);
    const countryOptions = options?.['countries'] ?? [];
    const matched = countryOptions.find(
      (opt) => String(opt.value) === String(parentValue),
    );

    return matched?.label != null ? String(matched.label) : '';
  }, [parent, options]);

  useEffect(() => {
    // Espejamos el valor derivado en el campo destino. Si no se resuelve,
    // queda vacío (no rompemos el render).
    setValue(field.name, autofilledLabel, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [autofilledLabel, field.name, setValue]);

  // El campo es de solo lectura si el JSON lo declara (`properties.readonly`)
  // o si todo el formulario está en modo lectura.
  const isReadOnly = Boolean(readOnly) || Boolean(field.properties?.readonly);

  const sharedProps = {
    ...field.properties,
    control,
    name: field.name,
    label: field.label,
    maxLength: field.validations?.maxLength,
    formatter:
      EXTRA_FORMATTERS[field?.properties?.formatter as FieldExtraFormatter] ||
      field?.properties?.formatter,
    rules: validationSchema(field.validations),
    placeholder: field?.placeholder,
    readOnly: isReadOnly,
  };

  if (field?.properties?.outlined) {
    return <TextOutlinedInput {...sharedProps} />;
  }

  return <TextInput {...sharedProps} />;
}

export default AutofillNationalityField;
